import * as React from 'react';
import EventStepper from 'Components/EventStepper';
import EventStepperGroup from 'Components/EventStepper/EventStepperGroup';
import InputHandler from 'Components/InputHandler';
import { isPartialDate, nowPartialDate, partialDateToValue, sortPartialDate } from 'neuro-utils';
import { StepperHeaderFormInputPair, StepperHeaderValuePair } from 'Components/EventStepper/components';
import Treatments from './components/Treatments';
import { TDCSContext } from '../..';
import TabContent from 'Components/TabContent';
import Treatment from './components/Treatment';
import { equals } from 'ramda';
import {
  addingTreatmentSessionDisabledReason,
  convertSymptomsAndLocationsToRatingFormat,
  findPrecedingDoctorsOrder,
  getSessionNumber,
  subjectOfTreatmentTitle,
} from 'Utility/ninmtUtil';
import { RenderAlerts } from 'Components/Alerts';
import PatientsRatings from './components/PatientsRatings';
import colors from '../../../../../config/theme/colors';
import FormRow from 'Components/FormRow';
import ActionButtonRounded from 'Components/ActionButtonRounded';
import TasklistDialog from './components/TaskListDialog';
import { styled } from '@mui/system';
import { actions } from 'Store/myapp/actions';
import { useDispatch } from 'react-redux';
import { Task_Progress } from 'neuro-schemas';
import { formatPartialDate } from 'Utility/date';
import { DateTime } from 'luxon';
import { filterActiveAndUpcomingTasks, partialDateFromStartInclusive, TASK_DATE_FORMAT } from 'Utility/tasklist';
import { filterDocumentsByType } from 'Utility/documentHandling';

const StyledRow = styled('div')({
  display: 'grid',
  gridTemplateColumns: '10fr 2fr',
  width: '100%',
});

type TTreatmentEfficiencyState =
  | 'treatmentEfficiencyNotStarted'
  | 'treatmentEfficiencyBeforePending'
  | 'treatmentEfficiencyBeforeCompleted'
  | 'treatmentEfficiencyAfterPending'
  | 'treatmentEfficiencyAfterCompleted'
  | undefined;

const Sessions = (): React.JSX.Element => {
  const tdcsContext = React.useContext(TDCSContext);
  const {
    formData,
    fm,
    doctorsOrders,
    contraIndications,
    symptomsAndLocations,
    setDoctorsOrdersReview,
    setAnchor,
    patientMySQStatus,
    taskLists,
    sortedAndMergedMyDocuments,
  } = tdcsContext;

  const dispatch = useDispatch();

  const currentTreatmentEfficiencyDocs = filterDocumentsByType(sortedAndMergedMyDocuments, [
    'treatmentEfficiency',
  ]).filter(d => partialDateToValue(d.date) === partialDateToValue(nowPartialDate()));
  const before = currentTreatmentEfficiencyDocs.find(d => d.timing === 'before');
  const after = currentTreatmentEfficiencyDocs.find(d => d.timing === 'after');

  const [loading, setLoading] = React.useState<boolean>(false);

  const fetchMyAppData = React.useCallback(() => actions.loadMyAppData(dispatch), [dispatch]);

  const currentTaskLists = taskLists.filter(
    tl =>
      tl.tasks.some(task => task.includes('treatmentEfficiency')) &&
      /** idk whats going on in here TODO also, the upper is not safe ^^ */
      partialDateToValue(partialDateFromStartInclusive(tl.start_inclusive)) === partialDateToValue(nowPartialDate()),
  );

  // Active tasklists are those whose deadline has not already passed
  const activeTaskLists = filterActiveAndUpcomingTasks({ tasklists: taskLists });

  // Reset tasklist deadline to now to make it inactive
  const closeTaskList = (task?: 'treatmentEfficiencyBefore' | 'treatmentEfficiencyAfter') => {
    // By default, only a tasklist with both treatmentEfficiencyBefore and treatmentEfficiencyAfter should be closed automatically
    const beforeAndOrAfterTaskList = activeTaskLists.find(tl =>
      task
        ? tl.tasks.includes(task)
        : tl.tasks.includes('treatmentEfficiencyBefore') && tl.tasks.includes('treatmentEfficiencyAfter'),
    );

    if (beforeAndOrAfterTaskList) {
      actions
        .updateTasklist(
          {
            ...beforeAndOrAfterTaskList,
            end_exclusive: DateTime.now().setZone(beforeAndOrAfterTaskList.timezone).toFormat(TASK_DATE_FORMAT),
          },
          dispatch,
        )
        .then(() => {
          return;
        })
        .catch(() => {
          return;
        });
    }
  };

  const refreshMyAppData = (): void => {
    setLoading(true);
    fetchMyAppData()
      .then(() => {
        setLoading(false);
      })
      .catch(() => {
        return;
      });
  };

  const [taskListDialogOpen, setTaskListDialogOpen] = React.useState<boolean>(false);

  const getTreatmentEfficiencyState = (): TTreatmentEfficiencyState => {
    // Some tasklists have been sent --> treatment efficiency procedure has started
    if (currentTaskLists.length > 0 || currentTreatmentEfficiencyDocs.length > 0) {
      // Some surveys have been filled --> the "after treatment" survey is either not started, pending or completed
      if (currentTreatmentEfficiencyDocs.length > 0) {
        const beforeCompleted =
          before &&
          Task_Progress.calculateProgress('treatmentEfficiency', before).state === Task_Progress.State.Completed;
        const afterCompleted =
          after &&
          Task_Progress.calculateProgress('treatmentEfficiency', after).state === Task_Progress.State.Completed;

        if (beforeCompleted) {
          // The "after treatment" survey is completed --> treatment efficiency procedure is completed
          if (afterCompleted) return 'treatmentEfficiencyAfterCompleted';
          if (activeTaskLists.some(tl => tl.tasks.some(task => task.indexOf('treatmentEfficiencyAfter') > -1))) {
            // The "before treatment" survey is completed and a new tasklist has been sent --> the "after treatment" survey is pending
            return 'treatmentEfficiencyAfterPending';
          }
          // The "before treatment" survey is completed and a new tasklist has not been sent --> the "after treatment" survey is not started
          return 'treatmentEfficiencyBeforeCompleted';
        }
      }
      if (activeTaskLists.some(tl => tl.tasks.some(task => task.indexOf('treatmentEfficiencyBefore') > -1))) {
        // No completed treatment efficiency surveys --> the "before treatment" survey is pending
        return 'treatmentEfficiencyBeforePending';
      }
      // Tasklists have been sent but none of them is active --> treatment efficiency procedure has not started
      return 'treatmentEfficiencyNotStarted';
    }
    // No tasklists have been sent --> treatment efficiency procedure has not started
    return 'treatmentEfficiencyNotStarted';
  };

  const [treatmentEfficiencyState, setTreatmentEfficiencyState] =
    React.useState<TTreatmentEfficiencyState>(getTreatmentEfficiencyState());

  // Refresh treatment efficiency state
  React.useEffect(() => {
    setTreatmentEfficiencyState(getTreatmentEfficiencyState());
  }, [taskLists, sortedAndMergedMyDocuments]);

  // Close tasklists when treatment efficiency procedure is completed
  React.useEffect(() => {
    if (treatmentEfficiencyState === 'treatmentEfficiencyAfterCompleted' && activeTaskLists.length > 0) {
      closeTaskList();
    }
  }, [treatmentEfficiencyState]);

  // If all doctor's orders are "completed", no review is required
  const noReviewRequired = doctorsOrders.every(d => d.completed);

  const buttonDisabledReason = addingTreatmentSessionDisabledReason('tdcs', doctorsOrders, contraIndications, formData);

  // Add session createDate for more accurate sorting
  const creationMutator = (
    event: ITDCSSession | ITDCSUnusedSession | ITDCSOtherEvent,
  ): ITDCSSession | ITDCSUnusedSession | ITDCSOtherEvent => {
    return { ...event, createDate: Date.now() };
  };

  // Optionally require subjects of treatment etc.
  const precedingDoctorsOrder = (index?: number, reqFields?: 'endTDCS'[]) =>
    findPrecedingDoctorsOrder('tdcs', doctorsOrders, formData.document.sessions ?? [], index ?? 0, reqFields);

  const precedingDocOrdBeforeAddingNewSes = React.useRef<IDoctorsOrder | undefined>(precedingDoctorsOrder(0, []));

  const [dateDefault, setDateDefault] = React.useState<'now' | PartialDate>('now');
  const [eventType, setEventType] = React.useState<string | undefined>(undefined);
  const [disableType, setDisableType] = React.useState<boolean>(false);

  const handleSessionTypeChange = (values: TOnChangeValues) => {
    const value = Object.values(values)[0];
    if (typeof value === 'string') setEventType(value);
  };

  // Capping the session date
  React.useEffect(() => {
    if (precedingDocOrdBeforeAddingNewSes.current && precedingDocOrdBeforeAddingNewSes.current.tdcs?.endTDCS) {
      setDateDefault(precedingDocOrdBeforeAddingNewSes.current.date ?? 'now');
    } else {
      // If preceding order document has not ended tdcs but future one has ended cap it to that
      const futureOrders = doctorsOrders.filter(
        d =>
          partialDateToValue(d.date) >
          partialDateToValue(formData.document.sessions?.[0]?.date ?? formData.document.date),
      );
      setDateDefault(futureOrders.find(o => o.tdcs?.endTDCS)?.date ?? 'now');
    }
  }, [precedingDocOrdBeforeAddingNewSes.current, formData.document.date]);

  const hasDeletedSessions =
    Array.isArray(formData.document.sessions) &&
    formData.document.sessions.length > 0 &&
    formData.document.sessions.some(s => s.removeTS ?? s.removeInfo);

  // Hide/filter deleted sessions
  React.useEffect(() => {
    if (hasDeletedSessions) {
      formData.onChange?.({ sessions: formData.document.sessions?.filter(s => !(s.removeTS ?? s.removeInfo)) });
    }
  }, [hasDeletedSessions]);

  // Initialize patient's rating based on current symptoms and locations
  const initializePatientsRating = (index: number, date?: PartialDate) => {
    const { document, onChange } = formData;

    const newSessions: Array<ITDCSSession> = structuredClone(document.sessions) ?? [];
    const newSession = newSessions[index];
    if (date && newSession) newSession.date = date;

    if (newSession?.date) {
      const newPatientsRating = convertSymptomsAndLocationsToRatingFormat(newSession, symptomsAndLocations);

      newSession.patientsRating = newPatientsRating;
      onChange && onChange({ sessions: newSessions });

      if (treatmentEfficiencyState === 'treatmentEfficiencyBeforePending') {
        closeTaskList('treatmentEfficiencyBefore');
      }
    }
  };

  return (
    <React.Fragment>
      <FormRow title="tdcs.newSession">
        <InputHandler
          name="typeOfEvent"
          type="Radio"
          options={['treatmentMonitoring', 'doctorInterview', 'nurseReception', 'session', 'unusedSession']}
          editing={true}
          formData={{
            document: { typeOfEvent: eventType },
            onChange: handleSessionTypeChange,
          }}
          optionFormatter={o => fm(`tdcs.opts.eventTypes.${o}`)}
          disabledOptions={
            disableType
              ? ['treatmentMonitoring', 'doctorInterview', 'nurseReception', 'session', 'unusedSession']
              : undefined
          }
        />
      </FormRow>
      <EventStepperGroup
        allClosed
        previousEventsCollapseLimit={5}
        previousEventsCollapseMessage={{ showMessage: 'tdcs.showAllSessions', hideMessage: 'tdcs.hideAllSessions' }}
      >
        <EventStepper
          name="sessions"
          formData={formData}
          mutators={{ creationMutator }}
          addNewOverrideAction={
            noReviewRequired
              ? undefined
              : () => {
                  // If all doctor's orders are not completed, review is required
                  setAnchor('sessionsAndProtocols');
                  setDoctorsOrdersReview({ active: true, completed: false });
                }
          }
          deleteDialogMode="full"
          stepLabelText={(d: ITDCSSession): string =>
            `${formatPartialDate(d.date)} - ${fm('tdcs.opts.eventTypes.session')}`
          }
          stepContent={(d: ITDCSSession): React.JSX.Element => (
            <React.Fragment>
              {['patientsRating', 'additionalInformation', 'subjectOfTreatment'].map((key, index) => {
                const currentIndex = formData.document.sessions?.findIndex(s => equals(s, d));
                switch (key) {
                  case 'patientsRating': {
                    const value = Array.isArray(d[key]) ? d[key] : [];
                    return value.filter(s => Boolean(s)).length > 0 || !Array.isArray(d.patientsRating) ? (
                      <React.Fragment key={`${key}${index}`}>
                        <StepperHeaderValuePair
                          header={
                            <div style={{ whiteSpace: 'nowrap', fontWeight: 400 }}>
                              {fm('tdcs.patientsRating.title')}
                            </div>
                          }
                          value={''}
                        />
                        <div style={{ margin: '2rem 0 2rem -2.1rem' }}>
                          <PatientsRatings
                            editIndex={currentIndex ?? 0}
                            editingEvent={false}
                            formData={formData}
                            fm={fm}
                            symptomsAndLocations={symptomsAndLocations}
                            sessionDate={d.date ?? undefined}
                          />
                        </div>
                      </React.Fragment>
                    ) : (
                      <React.Fragment key={`${key}${index}`} />
                    );
                  }
                  case 'subjectOfTreatment': {
                    const currentIndex = formData.document.sessions?.findIndex(s => equals(s, d));
                    const value = Array.isArray(d[key]) ? d[key] : [];
                    return value.filter(s => Boolean(s)).length > 0 ? (
                      <React.Fragment key={`${key}${index}`}>
                        <StepperHeaderValuePair header={fm('tdcs.subjectOfTreatment.title')} value={''} />
                        <div style={{ margin: '2rem 0 2rem -2.1rem' }}>
                          <TabContent>
                            {
                              value.map((s, i) => ({
                                key: `${s.id}${i}`,
                                id: `${s.id}Tab${i}`,
                                title: subjectOfTreatmentTitle(s, 'tdcs', fm),
                                ended: s.deleted,
                                content: (
                                  <Treatment
                                    document={s}
                                    onChange={(): string => ''}
                                    session={d}
                                    sessionNumber={getSessionNumber(
                                      formData.document.sessions ?? [],
                                      currentIndex ?? 0,
                                      s,
                                    )}
                                    precedingDoctorsOrder={precedingDoctorsOrder(currentIndex)}
                                    editingEvent={false}
                                  />
                                ),
                                count: i,
                              })) as Parameters<typeof TabContent>[0]['children']
                            }
                          </TabContent>
                        </div>
                      </React.Fragment>
                    ) : (
                      <React.Fragment key={`${key}${index}`} />
                    );
                  }
                  default: {
                    const value = d[key as keyof typeof d];
                    return (
                      <StepperHeaderValuePair
                        key={`${key}${index}`}
                        header={fm(`tdcs.${key === 'additionalInformation' ? 'additionalInformationSession' : key}`)}
                        value={value ?? '-'}
                      />
                    );
                  }
                }
              })}
            </React.Fragment>
          )}
          addNewTextHeader=""
          addNewTextButton="tdcs.newSession"
          buttonDisabled={buttonDisabledReason ? true : eventType !== 'session' ? true : false}
          hideNewButton={!(eventType === 'session' || !eventType)}
          buttonDisabledMessage={
            eventType !== 'session' && !buttonDisabledReason ? fm('tdcs.chooseEventType') : undefined
          }
          buttonDisabledElement={
            buttonDisabledReason ? (
              <RenderAlerts
                alerts={[{ id: 'tdcsAlert', textID: `tdcs.${buttonDisabledReason}Message`, severity: 'info' }]}
                elevation={0}
                width={80}
              />
            ) : undefined
          }
          cancelExtraAction={() => {
            setDisableType(false);
            setEventType(undefined);
          }}
          saveExtraAction={() => {
            setDisableType(false);
            setEventType(undefined);
          }}
          addNewExtraAction={() => setDisableType(true)}
          startEditExtraAction={() => setDisableType(true)}
          previousEventsTextHeader="tdcs.previousSessions"
          editingElements={(index: number, onChange: IFormData['onChange']): React.JSX.Element => {
            return (
              <React.Fragment>
                <StepperHeaderFormInputPair
                  header={fm('general.date')}
                  input={
                    <InputHandler
                      type="PartialDate"
                      editing={true}
                      name="date"
                      formData={{
                        onChange: (values: TOnChangeValues) => {
                          if (onChange) {
                            const name = Object.keys(values)[0];
                            if (!name) return;
                            const value = values[name];

                            const currentSession = formData.document.sessions?.[index];

                            // Patient's rating should be re-initialized when session date is changed
                            if (
                              isPartialDate(value) &&
                              sortPartialDate(value, dateDefault === 'now' ? nowPartialDate() : dateDefault) !== 0
                            ) {
                              initializePatientsRating(index, value);
                            } else if (
                              isPartialDate(value) &&
                              sortPartialDate(value, dateDefault === 'now' ? nowPartialDate() : dateDefault) === 0 &&
                              currentSession?.patientsRating
                            ) {
                              onChange({ [name]: value, patientsRating: undefined });
                            } else {
                              onChange(values);
                            }
                          }
                        },
                        document: { date: formData.document.sessions?.[index]?.date ?? '' },
                      }}
                      dateDefault={dateDefault}
                      isNotCancellable={true}
                      dateHook={{
                        dateHookFloor: formData.document.date ?? undefined,
                        dateHookCeiling: precedingDoctorsOrder(0, [])?.tdcs?.endTDCS
                          ? (precedingDoctorsOrder(0, [])?.date ?? undefined)
                          : undefined,
                      }}
                    />
                  }
                />
                <StepperHeaderFormInputPair
                  header={<div style={{ whiteSpace: 'nowrap' }}>{fm('tdcs.patientsRating.title')}</div>}
                  input={<></>}
                />
                <div style={{ margin: '2rem 0 2rem -2.1rem' }}>
                  <PatientsRatings
                    editIndex={index}
                    formData={formData}
                    fm={fm}
                    symptomsAndLocations={symptomsAndLocations}
                    sessionDate={formData.document.sessions?.[index]?.date ?? undefined}
                    initializePatientsRating={initializePatientsRating}
                  />
                </div>
                <StepperHeaderFormInputPair
                  header={fm('tdcs.additionalInformationSession')}
                  input={
                    <InputHandler
                      type="TextArea"
                      editing={true}
                      name="additionalInformation"
                      formData={{
                        onChange,
                        document: {
                          additionalInformation: formData.document.sessions?.[index]?.additionalInformation ?? '',
                        },
                      }}
                      placeholder="tdcs.additionalInformationSession"
                    />
                  }
                />
                <StepperHeaderFormInputPair header={fm('tdcs.subjectOfTreatment.title')} input={<></>} />
                <div style={{ margin: '2rem 0 2rem -2.1rem' }}>
                  <Treatments editIndex={index} formData={formData} fm={fm} doctorsOrders={doctorsOrders} />
                </div>
              </React.Fragment>
            );
          }}
          stepperExtraElement={(index: number) => {
            const state = treatmentEfficiencyState;

            const currentSession = formData.document.sessions?.[index];

            // Should not be able to send tasklists if...
            if (
              // There is no current session
              !currentSession ||
              // Current session date is not current date
              partialDateToValue(currentSession.date) !== partialDateToValue(nowPartialDate()) ||
              // There is an existing patientsRating
              currentSession.patientsRating ||
              // Patient does not use My service
              !patientMySQStatus ||
              // There are no symptoms and locations
              symptomsAndLocations.filter(sl => sl.symptom !== 'other' || !sl.mentalSymptom).length === 0 ||
              // Both before and after surveys have been filled
              state === 'treatmentEfficiencyAfterCompleted'
            ) {
              return <React.Fragment />;
            }

            let text;
            let buttonText;
            let buttonAction;

            switch (state) {
              case 'treatmentEfficiencyNotStarted':
                text = 'tdcs.treatmentEfficiencyBeforeInfo';
                buttonText = 'tdcs.askPatient';
                buttonAction = () => setTaskListDialogOpen(true);
                break;
              case 'treatmentEfficiencyBeforePending':
                text = 'tdcs.treatmentEfficiencyBeforeActiveInfo';
                buttonText = 'tdcs.update';
                buttonAction = () => refreshMyAppData();
                break;
              case 'treatmentEfficiencyAfterPending':
                text = 'tdcs.treatmentEfficiencyAfterActiveInfo';
                buttonText = 'tdcs.update';
                buttonAction = () => refreshMyAppData();
                break;
              case 'treatmentEfficiencyBeforeCompleted':
                text = 'tdcs.treatmentEfficiencyAfterInfo';
                buttonText = 'tdcs.askPatient';
                buttonAction = () => setTaskListDialogOpen(true);
                break;
              // Unused case - element should not be rendered
              default:
                text = 'tdcs.treatmentEfficiencyBeforeInfo';
                buttonText = 'myService.newTaskList';
                buttonAction = () => setTaskListDialogOpen(true);
            }

            return (
              <div style={{ margin: '2rem 0 2rem 0' }}>
                <RenderAlerts
                  alerts={[
                    {
                      id: 'treatmentEfficiencyAlert',
                      textID: '',
                      customText: (
                        <React.Fragment>
                          <StyledRow style={{ marginBottom: '2rem' }}>
                            <div style={{ gridColumn: '1 / span 2', whiteSpace: 'pre-line' }}>{fm(text)}</div>
                          </StyledRow>
                          <StyledRow>
                            <div />
                            <div style={{ display: 'flex' }}>
                              {['treatmentEfficiencyNotStarted', 'treatmentEfficiencyBeforePending'].includes(
                                state ?? '',
                              ) && (
                                <div style={{ marginRight: '1rem' }}>
                                  <ActionButtonRounded
                                    text="tdcs.recordDataManually"
                                    onClick={() => initializePatientsRating(index)}
                                    loading={loading}
                                    colorScheme="dark"
                                    uppercase={false}
                                    fontSize={16}
                                    height={4}
                                    width={16}
                                    border={false}
                                  />
                                </div>
                              )}
                              <ActionButtonRounded
                                text={buttonText}
                                onClick={buttonAction}
                                loading={loading}
                                colorScheme="dark"
                                uppercase={false}
                                fontSize={16}
                                height={4}
                                width={16}
                              />
                            </div>
                          </StyledRow>
                        </React.Fragment>
                      ),
                      severity: 'info',
                    },
                  ]}
                  elevation={0}
                  width={80}
                />
              </div>
            );
          }}
        />
        <EventStepper
          name="unusedSessions"
          formData={formData}
          mutators={{ creationMutator }}
          stepLabelText={(d: ITDCSUnusedSession): string =>
            `${formatPartialDate(d.date)} - ${fm('tdcs.opts.eventTypes.unusedSession')}`
          }
          stepContent={(d: ITDCSUnusedSession): React.JSX.Element => (
            <React.Fragment>
              <StepperHeaderValuePair
                header={fm('tdcs.unusedSessionReason')}
                value={d.reason ? fm(`tdcs.opts.unusedSessionReason.${d.reason}`) : '-'}
              />
              {d.reason && (
                <React.Fragment>
                  <StepperHeaderValuePair
                    header={fm('tdcs.unusedSessionReasonDetails')}
                    value={d.reasonDetails ? fm(`tdcs.opts.unusedSessionReasonDetails.${d.reasonDetails}`) : '-'}
                  />
                  {d.reasonDetails === 'other' && (
                    <StepperHeaderValuePair
                      header={fm('tdcs.unusedSessionReasonDetailsOther')}
                      value={d.reasonDetailsOther && d.reasonDetailsOther.length > 0 ? d.reasonDetailsOther : '-'}
                    />
                  )}
                </React.Fragment>
              )}
            </React.Fragment>
          )}
          addNewTextHeader=""
          addNewTextButton="tdcs.newSession"
          buttonDisabled={eventType !== 'unusedSession'}
          hideNewButton={eventType !== 'unusedSession'}
          buttonDisabledMessage={fm('tdcs.chooseEventType')}
          previousEventsTextHeader="tdcs.previousUnusedSessions"
          editingElements={(index: number, onChange: IFormData['onChange']): React.JSX.Element => {
            const patientInducedReasonDetails = [
              'forgotten',
              'sick',
              'blockedByOtherReason',
              'confusionAboutSessionTime',
              'unknown',
              'other',
            ];
            const unitInducedReasonDetails = [
              'staffShortage',
              'deviceRelated',
              'cancelledDueToEmergencyPatient',
              'confusionInSessionBooking',
              'other',
            ];

            return (
              <React.Fragment>
                <StepperHeaderFormInputPair
                  header={fm('general.date')}
                  input={
                    <InputHandler
                      type="PartialDate"
                      editing={true}
                      name="date"
                      formData={{
                        onChange,
                        document: { date: formData.document.unusedSessions?.[index]?.date ?? '' },
                      }}
                      dateDefault={dateDefault}
                      isNotCancellable={true}
                      dateHook={{
                        dateHookFloor: formData.document.date ?? undefined,
                        dateHookCeiling: precedingDoctorsOrder(0, [])?.tdcs?.endTDCS
                          ? (precedingDoctorsOrder(0, [])?.date ?? undefined)
                          : undefined,
                      }}
                    />
                  }
                />
                <StepperHeaderFormInputPair
                  header={fm('tdcs.unusedSessionReason')}
                  input={
                    <InputHandler
                      type="Radio"
                      editing={true}
                      name="reason"
                      formData={{
                        onChange,
                        document: {
                          reason: formData.document.unusedSessions?.[index]?.reason ?? '',
                          reasonDetails: formData.document.unusedSessions?.[index]?.reasonDetails,
                          reasonDetailsOther: formData.document.unusedSessions?.[index]?.reasonDetailsOther,
                        },
                      }}
                      options={['patientInduced', 'unitInduced']}
                      optionFormatter={(id: string | number) => fm(`tdcs.opts.unusedSessionReason.${id}`)}
                      dependentFieldsRemovalWarning={true}
                      dependentFieldsList={
                        formData.document.unusedSessions?.[index]?.reasonDetails !== 'other'
                          ? () => ['reasonDetails', 'reasonDetailsOther']
                          : undefined
                      }
                    />
                  }
                />
                {formData.document.unusedSessions?.[index]?.reason && (
                  <React.Fragment>
                    <StepperHeaderFormInputPair
                      header={fm('tdcs.unusedSessionReasonDetails')}
                      input={
                        <InputHandler
                          type="Radio"
                          editing={true}
                          name="reasonDetails"
                          formData={{
                            onChange,
                            document: {
                              reasonDetails: formData.document.unusedSessions[index].reasonDetails ?? '',
                              reasonDetailsOther: formData.document.unusedSessions[index].reasonDetailsOther,
                            },
                          }}
                          options={
                            formData.document.unusedSessions[index].reason === 'patientInduced'
                              ? patientInducedReasonDetails
                              : unitInducedReasonDetails
                          }
                          optionFormatter={(id: string | number) => fm(`tdcs.opts.unusedSessionReasonDetails.${id}`)}
                          dependentFieldsRemovalWarning={true}
                          dependentFieldsList={() => ['reasonDetailsOther']}
                        />
                      }
                    />
                    {formData.document.unusedSessions[index].reasonDetails === 'other' && (
                      <StepperHeaderFormInputPair
                        header={fm('tdcs.unusedSessionReasonDetailsOther')}
                        input={
                          <InputHandler
                            type="TextArea"
                            editing={true}
                            name="reasonDetailsOther"
                            formData={{
                              onChange,
                              document: {
                                reasonDetailsOther: formData.document.unusedSessions[index].reasonDetailsOther ?? '',
                              },
                            }}
                            placeholder="tdcs.unusedSessionReasonDetailsOther"
                          />
                        }
                      />
                    )}
                  </React.Fragment>
                )}
              </React.Fragment>
            );
          }}
          theme={{ highlightColor: colors.highlight.light }}
        />
        <EventStepper
          name="otherEvents"
          formData={formData}
          mutators={{ creationMutator }}
          stepLabelText={(d: ITDCSOtherEvent): string =>
            `${formatPartialDate(d.date)} - ${fm(`tdcs.opts.eventTypes.${d.type}`)}`
          }
          stepContent={(d: ITDCSOtherEvent): React.JSX.Element => (
            <React.Fragment>
              <StepperHeaderValuePair
                header={fm('tdcs.typeOfEvent')}
                value={d.type ? fm(`tdcs.opts.eventTypes.${d.type}`) : '-'}
              />
              <React.Fragment>
                <StepperHeaderValuePair header={fm('tdcs.details')} value={d.details ?? '-'} />
              </React.Fragment>
            </React.Fragment>
          )}
          addNewTextHeader=""
          addNewTextButton="tdcs.newSession"
          buttonDisabled={!['treatmentMonitoring', 'doctorInterview', 'nurseReception'].includes(eventType ?? '')}
          buttonDisabledMessage={fm('tdcs.chooseEventType')}
          previousEventsTextHeader="tdcs.previousSessions"
          hideNewButton={!['treatmentMonitoring', 'doctorInterview', 'nurseReception'].includes(eventType ?? '')}
          editingElements={(index: number, onChange: IFormData['onChange']): React.JSX.Element => {
            if (!formData.document.otherEvents?.[index]?.type) {
              onChange?.({ type: eventType });
            }
            return (
              <React.Fragment>
                <StepperHeaderFormInputPair
                  header={fm('general.date')}
                  input={
                    <InputHandler
                      type="PartialDate"
                      editing={true}
                      name="date"
                      formData={{
                        onChange,
                        document: { date: formData.document.otherEvents?.[index]?.date ?? '' },
                      }}
                      dateDefault={dateDefault}
                      isNotCancellable={true}
                      dateHook={{
                        dateHookFloor: formData.document.date ?? undefined,
                        dateHookCeiling: precedingDoctorsOrder(0, [])?.tdcs?.endTDCS
                          ? (precedingDoctorsOrder(0, [])?.date ?? undefined)
                          : undefined,
                      }}
                    />
                  }
                />
                <StepperHeaderFormInputPair
                  header={fm('tdcs.details')}
                  input={
                    <InputHandler
                      type="TextArea"
                      editing={true}
                      name="details"
                      formData={{
                        onChange,
                        document: {
                          details: formData.document.otherEvents?.[index]?.details ?? '',
                        },
                      }}
                    />
                  }
                />
              </React.Fragment>
            );
          }}
        />
      </EventStepperGroup>
      {treatmentEfficiencyState && (
        <TasklistDialog
          taskActionType={
            treatmentEfficiencyState === 'treatmentEfficiencyNotStarted'
              ? 'tdcsTreatmentEfficiencyBefore'
              : 'tdcsTreatmentEfficiencyAfter'
          }
          sessionDate={nowPartialDate()}
          dialogOpen={taskListDialogOpen}
          setDialogOpen={setTaskListDialogOpen}
          activeTaskLists={activeTaskLists}
          fm={fm}
        />
      )}
    </React.Fragment>
  );
};

export default Sessions;
